import { Link } from "react-router-dom";
import { splitFindingRefs } from "@/lib/finding-refs";
import { markdownPreview } from "@/lib/plain-text";
import { type StepRefSegment, splitStepRefs } from "@/lib/step-refs";

const LINK_CLASS =
  "underline decoration-border underline-offset-4 hover:decoration-foreground";

type StepSegment = Extract<StepRefSegment, { kind: "step" }>;

/**
 * Judge reasoning cites findings by id and steps by number so it stays
 * falsifiable. Readers need the evidence, not the reference:
 *
 * - each known finding id becomes a link carrying the finding's title;
 *   unknown ids are dropped (a raw UUID tells a reader nothing);
 * - given the run's `walkthroughId`, each step reference ("step-5",
 *   "steps 3, 4, 9", "steps 2-5") links to the run's execution trace
 *   opened on that step (`?step=N`).
 *
 * A range opens its first step. Without a run, step references stay text.
 */
export function ReasoningText({
  text,
  findings,
  projectId,
  walkthroughId,
  onSelectFinding,
}: {
  text: string;
  findings: ReadonlyMap<string, string>;
  projectId: string;
  /** The run the reasoning belongs to — step refs link to it off-page. */
  walkthroughId?: string | null;
  onSelectFinding?: (findingId: string) => void;
}) {
  let renderStep: ((seg: StepSegment) => React.ReactNode) | null = null;
  if (walkthroughId && projectId) {
    renderStep = (seg) => (
      <Link
        to={`/projects/${projectId}/walkthroughs/${walkthroughId}?step=${seg.stepNumber}`}
        className={LINK_CLASS}
      >
        <StepLabel seg={seg} />
      </Link>
    );
  }

  const parts: React.ReactNode[] = [];
  let offset = 0;
  for (const seg of splitFindingRefs(text, findings)) {
    if (seg.kind === "text") {
      parts.push(...linkSteps(seg.text, renderStep, offset));
      offset += seg.text.length;
      continue;
    }
    parts.push(
      <Link
        key={`finding-${offset}-${seg.id}`}
        to={`/projects/${projectId}/findings/${seg.id}`}
        onClick={(e) => {
          if (
            onSelectFinding &&
            !e.metaKey &&
            !e.ctrlKey &&
            !e.shiftKey &&
            !e.altKey &&
            e.button === 0
          ) {
            e.preventDefault();
            onSelectFinding(seg.id);
          }
        }}
        className={LINK_CLASS}
      >
        “{seg.title}”
      </Link>,
    );
    offset += 1;
  }
  return <>{parts}</>;
}

/**
 * A bare list number ("4" in "steps 3, 4") gets "Step" for screen readers,
 * so every control's name says which step it opens.
 */
function StepLabel({ seg }: { seg: StepSegment }) {
  const bare = /^\d/.test(seg.text);
  return (
    <>
      {bare && <span className="sr-only">Step </span>}
      {seg.text}
    </>
  );
}

function linkSteps(
  text: string,
  renderStep: ((seg: StepSegment) => React.ReactNode) | null,
  offset: number,
): React.ReactNode[] {
  if (!renderStep) return [text];
  const nodes: React.ReactNode[] = [];
  const segments = splitStepRefs(text);
  let pos = offset;
  let pending = ""; // text not yet emitted
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const at = pos;
    pos += seg.text.length;
    const control = seg.kind === "step" ? renderStep(seg) : null;
    if (!control) {
      pending += seg.text;
      continue;
    }
    // A control is an atomic inline, so the line may break between "("
    // and it. Keep the punctuation hugging it on the same line.
    const lead = /\S*$/.exec(pending)?.[0] ?? "";
    nodes.push(pending.slice(0, pending.length - lead.length));
    const next = segments[i + 1];
    const trail =
      next?.kind === "text" ? (/^[^\s\w]*/.exec(next.text)?.[0] ?? "") : "";
    if (next?.kind === "text" && trail) {
      next.text = next.text.slice(trail.length);
      pos += trail.length;
    }
    pending = "";
    nodes.push(
      <span key={`step-${at}`} className="whitespace-nowrap">
        {lead}
        {control}
        {trail}
      </span>,
    );
  }
  nodes.push(pending);
  return nodes;
}

/**
 * Finding id (lower-case) → display title, for ReasoningText links.
 * Accepts either list shape `finding.list` returns; rows without any
 * title or description are left out, so their ids are dropped.
 */
export function findingTitleMap(
  findings: ReadonlyArray<{
    id: string;
    title?: string | null;
    description?: string | null;
  }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const f of findings) {
    const title = f.title || markdownPreview(f.description ?? "");
    if (title) map.set(f.id.toLowerCase(), title);
  }
  return map;
}
